/**
 * Converts between the stored hours string (e.g. "9:00 AM – 9:00 PM" / "Closed")
 * and the editor's dropdown state. Kept colocated with HoursSectionEditor so the
 * storage format change is encapsulated.
 *
 * Storage stays as Record<string, string> — this layer just decides how strings
 * map to dropdown values and back.
 */

export interface HoursDropdownState {
  open: string;   // "9:00 AM"
  close: string;  // "9:00 PM"
  closed: boolean;
}

export const DEFAULT_HOURS_STATE: HoursDropdownState = {
  open: "9:00 AM",
  close: "9:00 PM",
  closed: false,
};

const TIME_TOKEN = /(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)?/;

/**
 * Normalizes a single time token into our canonical "H:MM AM/PM" form.
 * Returns null if the token can't be confidently parsed.
 */
function normalizeTimeToken(raw: string): string | null {
  const trimmed = raw.trim();
  const match = trimmed.match(TIME_TOKEN);
  if (!match) return null;

  let hour = parseInt(match[1], 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;
  const periodRaw = match[3]?.toUpperCase();

  if (Number.isNaN(hour) || hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;

  let period: "AM" | "PM";
  if (periodRaw === "AM" || periodRaw === "PM") {
    period = periodRaw;
    if (hour < 1 || hour > 12) return null;
  } else {
    // No AM/PM marker — interpret as 24-hour
    period = hour < 12 ? "AM" : "PM";
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
  }

  return `${hour}:${minute.toString().padStart(2, "0")} ${period}`;
}

/**
 * Parses any saved string into a dropdown state.
 * Falls back to DEFAULT_HOURS_STATE for unrecognized inputs so the UI is
 * always in a usable state.
 */
export function parseHoursString(raw: string | undefined): HoursDropdownState {
  if (!raw || typeof raw !== "string") return { ...DEFAULT_HOURS_STATE };

  const trimmed = raw.trim();
  if (!trimmed) return { ...DEFAULT_HOURS_STATE };
  if (trimmed.toLowerCase() === "closed") {
    return { ...DEFAULT_HOURS_STATE, closed: true };
  }

  // Accept any of: " - ", " – ", " — ", " to "
  const parts = trimmed.split(/\s*(?:–|—|-|to)\s*/i);
  if (parts.length < 2) return { ...DEFAULT_HOURS_STATE };

  const open = normalizeTimeToken(parts[0]);
  const close = normalizeTimeToken(parts[1]);
  if (!open || !close) return { ...DEFAULT_HOURS_STATE };

  return { open, close, closed: false };
}

/**
 * Formats a dropdown state back into the canonical stored string.
 */
export function formatHoursString(state: HoursDropdownState): string {
  if (state.closed) return "Closed";
  return `${state.open} – ${state.close}`;
}
